import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, StyleSheet, Platform, TouchableOpacity, Text, ScrollView, ActivityIndicator, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, FormHeader } from '../components';
import { FlowForm, FlowFormData } from '../components/flows/FlowForm';
import { FlowVersionHistory } from '../components/flows/FlowVersionHistory';
import { FlowRunHighlight, runHighlightFromSteps } from '../components/flows/flow-types';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useFlowPermissions } from '../hooks/useFlowPermissions';

interface FlowEditScreenProps {
  flowId?: string;
}

const isWeb = Platform.OS === 'web';

export const FlowEditScreen: React.FC<FlowEditScreenProps> = ({ flowId }) => {
  // View-only for people without edit access; publishing needs admin.
  const { canEdit, canAdmin } = useFlowPermissions();
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  // Opened from Flow runs → a run: its path is drawn over the canvas until cleared.
  const runId = (routeParams as any)?.runId as string | undefined;
  const [runHighlight, setRunHighlight] = useState<FlowRunHighlight | null>(null);
  useEffect(() => {
    if (!runId || !flowId) { setRunHighlight(null); return; }
    let alive = true;
    apiService.getFlowRun(runId).then((res) => {
      if (!alive || res.statusCode !== 1000 || !res.data || res.data.flowId !== flowId) return;
      const run = res.data;
      const who = run.contactName || run.contactPhone || `contact ${run.contactId.slice(-6)}`;
      setRunHighlight(runHighlightFromSteps(`${who} · ${new Date(run.startedAt).toLocaleString()}`, run.steps));
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [runId, flowId]);
  const isEditing = !!flowId;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState<any>(null);
  const formSubmitRef = useRef<(() => void) | null>(null);

  // On web, FlowForm pushes its "flow preview" and "settings / selected-node editor" JSX up
  // here via effects, so this screen can place them in ScreenLayout's real left/right panes
  // (the same 355px panes every other screen — contacts, settings, item details — uses)
  // instead of FlowForm inventing its own layout.
  const [previewPaneContent, setPreviewPaneContent] = useState<React.ReactNode>(null);
  const [sidebarPaneContent, setSidebarPaneContent] = useState<React.ReactNode>(null);
  const handlePreviewPaneChange = useCallback((node: React.ReactNode) => setPreviewPaneContent(node), []);
  const handleSidebarPaneChange = useCallback((node: React.ReactNode) => setSidebarPaneContent(node), []);

  // The canvas needs all the vertical space it can get, so on web the flow editor drops the
  // app's fixed header/nav entirely (see ScreenLayout — header is optional, and
  // showBottomTabs is already false on web) and puts its own slim toolbar (back, flow name,
  // preview toggle, Cancel/Save) directly above the canvas instead.
  const [previewVisible, setPreviewVisible] = useState(true);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveMenuPosition, setSaveMenuPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const saveMenuCaretRef = useRef<View>(null);

  useEffect(() => {
    if (isEditing) {
      loadFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);

  const loadFlow = async (silent = false) => {
    if (!flowId) return;
    try {
      if (!silent) setLoading(true);
      const response = await apiService.getFlowById(flowId);
      if (response.statusCode === 1000 && response.data) {
        setFlow(response.data);
      } else if (!silent) {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to load flow');
        navigateTo('flows-list');
      }
    } catch (error: any) {
      console.error('Error loading flow:', error);
      if (!silent) {
        showNotification('error', 'Error', error.message || 'Failed to load flow');
        navigateTo('flows-list');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handlePublish = async () => {
    setSaveMenuOpen(false);
    if (!flowId) return;
    try {
      setPublishing(true);
      const response = await apiService.publishFlow(flowId);
      if (response.statusCode === 1000) {
        showNotification('success', 'Published', 'This flow is now live. In-progress conversations keep running on whichever version they started on.');
        setFlow(response.data);
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to publish flow');
      }
    } catch (error: any) {
      console.error('Error publishing flow:', error);
      showNotification('error', 'Error', error.message || 'Failed to publish flow');
    } finally {
      setPublishing(false);
    }
  };

  const handleSubmit = async (formData: FlowFormData) => {
    try {
      setSaving(true);
      if (isEditing && flowId) {
        const response = await apiService.updateFlow(flowId, {
          name: formData.name,
          description: formData.description,
          nodes: formData.nodes,
          entryNodeId: formData.entryNodeId,
          isActive: formData.isActive,
          sessionTimeoutMinutes: formData.sessionTimeoutMinutes ?? null,
          stopOnAgentReply: formData.stopOnAgentReply === true,
        });
        if (response.statusCode === 1000) {
          showNotification('success', 'Success', 'Flow updated successfully.');
          // Stay on the edit screen (matching the create branch below) — saving a draft isn't
          // "done with this flow", the business is very likely about to keep editing or hit
          // Publish next, so bouncing back to the list would just make them navigate right
          // back in. Silent re-fetch (no loading spinner/error redirect) so the screen reflects
          // the just-saved draft/version state without disrupting the in-progress editor.
          loadFlow(true);
        } else {
          showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to update flow');
        }
      } else {
        const response = await apiService.createFlow({
          name: formData.name,
          description: formData.description,
          nodes: formData.nodes,
          entryNodeId: formData.entryNodeId,
          isActive: formData.isActive,
          sessionTimeoutMinutes: formData.sessionTimeoutMinutes ?? null,
          stopOnAgentReply: formData.stopOnAgentReply === true,
        });
        if (response.statusCode === 1000) {
          showNotification('success', 'Success', 'Flow created successfully.');
          // Drop straight into edit mode for the flow just created (draft/publish, versions,
          // etc. only make sense once a flow exists) instead of bouncing back to the list —
          // matches navigateTo('flow-edit', id)'s existing param wiring (AppNavigator.tsx).
          const newFlowId = response.data?._id;
          if (newFlowId) {
            navigateTo('flow-edit', String(newFlowId));
          } else {
            handleBack();
          }
        } else {
          showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to create flow');
        }
      }
    } catch (error: any) {
      console.error('Error saving flow:', error);
      showNotification('error', 'Error', error.message || 'Failed to save flow');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('flows-list');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loading) {
    return (
      <ScreenLayout
        header={
          isWeb ? undefined : (
            <Header
              showNotifications={true}
              showMessages={true}
              onNotificationPress={() => navigateTo('notifications')}
              onMessagePress={() => navigateTo('chat')}
              showNavigation={false}
              activeTab={activeTab}
              onTabPress={handleTabPress}
            />
          )
        }
        headerHeight={isWeb ? 0 : undefined}
        showBottomTabs={!isWeb}
        showLeftPane={false}
        showRightPane={false}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading flow...</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (isEditing && !flow) {
    return null;
  }

  return (
    <ScreenLayout
      header={
        isWeb ? undefined : (
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={false}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        )
      }
      headerHeight={isWeb ? 0 : undefined}
      showBottomTabs={!isWeb}
      showLeftPane={isWeb && previewVisible}
      onLeftPaneClose={() => {}}
      leftPaneContent={isWeb ? 'custom' : 'settings'}
      leftPaneCustomContent={previewPaneContent}
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={isWeb}
      onRightPaneClose={() => {}}
      rightPaneContent={isWeb ? 'custom' : 'empty'}
      rightPaneCustomContent={sidebarPaneContent}
      formHeader={
        !isWeb ? (
          <FormHeader
            title={isEditing ? 'Edit Flow' : 'New Flow'}
            subtitle="Build a reusable decision tree"
            onBack={handleBack}
            actions={[
              {
                icon: 'checkmark',
                onPress: () => formSubmitRef.current?.(),
                primary: true,
                loading: saving,
                disabled: saving,
              },
            ]}
          />
        ) : undefined
      }
    >
      {isWeb ? (
        <View style={styles.webContainer}>
          <View style={styles.webToolbar}>
            <View style={styles.webToolbarLeft}>
              <TouchableOpacity style={styles.backButton} onPress={handleBack}>
                <DirectionalIcon name="arrow-back" size={22} color={colors.text.primary} />
              </TouchableOpacity>
              <Text style={styles.toolbarTitle} numberOfLines={1}>{isEditing ? 'Edit Flow' : 'New Flow'}</Text>
              {isEditing && flow && (
                <View style={[styles.statusBadge, flow.hasUnpublishedChanges ? styles.statusBadgeDraft : styles.statusBadgePublished]}>
                  <Text style={[styles.statusBadgeText, flow.hasUnpublishedChanges ? styles.statusBadgeTextDraft : styles.statusBadgeTextPublished]}>
                    {flow.hasUnpublishedChanges ? 'Unpublished changes' : 'Published'}
                  </Text>
                </View>
              )}
            </View>
            <View style={styles.webToolbarRight}>
              {isEditing && (
                <TouchableOpacity style={styles.circleButton} onPress={() => setVersionHistoryOpen(true)} activeOpacity={0.8}>
                  <Ionicons name="time-outline" size={18} color={colors.primary as string} />
                </TouchableOpacity>
              )}
              {isEditing && (
                <TouchableOpacity accessibilityLabel="Runs of this flow" style={styles.circleButton} onPress={() => navigateTo('dashboard-flows-all', undefined, { flowId })} activeOpacity={0.8}>
                  <Ionicons name="pulse-outline" size={18} color={colors.primary as string} />
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.circleButton} onPress={() => setPreviewVisible((v) => !v)} activeOpacity={0.8}>
                <Ionicons name={previewVisible ? 'eye-off-outline' : 'eye-outline'} size={18} color={colors.primary as string} />
              </TouchableOpacity>
              {!canEdit ? null : isEditing ? (
                <View style={styles.menuWrap}>
                  <TouchableOpacity style={styles.circleButton} onPress={() => formSubmitRef.current?.()} disabled={saving} activeOpacity={0.8}>
                    {saving ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Ionicons name="save-outline" size={18} color={colors.primary as string} />}
                  </TouchableOpacity>
                  {canAdmin && <TouchableOpacity
                    ref={saveMenuCaretRef}
                    style={styles.circleButtonCaret}
                    onPress={() => {
                      saveMenuCaretRef.current?.measureInWindow((x, y, width, height) => {
                        setSaveMenuPosition({ x, y, width, height });
                        setSaveMenuOpen(true);
                      });
                    }}
                    activeOpacity={0.8}
                  >
                    <Ionicons name="chevron-down" size={12} color={colors.textLight as string} />
                  </TouchableOpacity>}
                  <Modal visible={saveMenuOpen} transparent animationType="fade" onRequestClose={() => setSaveMenuOpen(false)}>
                    <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={() => setSaveMenuOpen(false)}>
                      <View
                        style={[
                          styles.modalMenuPositioner,
                          saveMenuPosition
                            ? { top: saveMenuPosition.y + saveMenuPosition.height + 6, left: saveMenuPosition.x + saveMenuPosition.width - 170 }
                            : null,
                        ]}
                      >
                        <View style={styles.menuDropdown}>
                          <TouchableOpacity style={styles.menuItem} onPress={() => { setSaveMenuOpen(false); handlePublish(); }} disabled={publishing}>
                            {publishing ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Ionicons name="rocket-outline" size={16} color={colors.text.primary as string} />}
                            <Text style={styles.menuItemText}>Publish</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </TouchableOpacity>
                  </Modal>
                </View>
              ) : (
                <TouchableOpacity style={styles.circlePrimaryButton} onPress={() => formSubmitRef.current?.()} disabled={saving} activeOpacity={0.8}>
                  {saving ? <ActivityIndicator size="small" color={colors.white as string} /> : <Ionicons name="checkmark" size={20} color={colors.white as string} />}
                </TouchableOpacity>
              )}
            </View>
          </View>
          {runHighlight && (
            <View style={styles.runBanner}>
              <Ionicons name="pulse-outline" size={16} color={colors.primary as string} />
              <Text style={styles.runBannerText} numberOfLines={1}>Showing the path of a run: {runHighlight.label}. Numbers are the order of steps; red = error, amber = waiting.</Text>
              <TouchableOpacity onPress={() => setRunHighlight(null)} hitSlop={8}>
                <Text style={styles.runBannerClear}>Hide</Text>
              </TouchableOpacity>
            </View>
          )}
          <View style={styles.webFormArea}>
            <FlowForm
              initialData={{
                _id: flow?._id,
                name: flow?.name,
                description: flow?.description,
                nodes: flow?.nodes,
                entryNodeId: flow?.entryNodeId,
                isActive: flow?.isActive,
              }}
              onSubmit={handleSubmit}
              onCancel={handleBack}
              submitLabel={isEditing ? 'Save Changes' : 'Create Flow'}
              loading={saving}
              hideActions
              submitRef={formSubmitRef}
              onPreviewPaneChange={handlePreviewPaneChange}
              onSidebarPaneChange={handleSidebarPaneChange}
              onValidationError={(message) => showNotification('error', 'Fix before saving', message)}
              runHighlight={runHighlight}
            />
          </View>
        </View>
      ) : (
        <View style={styles.wrapper}>
          <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
            <View style={styles.formContainer}>
              <FlowForm
                initialData={{
                  _id: flow?._id,
                  name: flow?.name,
                  description: flow?.description,
                  nodes: flow?.nodes,
                  entryNodeId: flow?.entryNodeId,
                  isActive: flow?.isActive,
                }}
                onSubmit={handleSubmit}
                onCancel={handleBack}
                submitLabel={isEditing ? 'Save Changes' : 'Create Flow'}
                loading={saving}
                hideActions
                submitRef={formSubmitRef}
                onValidationError={(message) => showNotification('error', 'Fix before saving', message)}
              />
            </View>
          </ScrollView>
        </View>
      )}
      {isEditing && flowId && (
        <FlowVersionHistory
          visible={versionHistoryOpen}
          onClose={() => setVersionHistoryOpen(false)}
          flowId={flowId}
          onChanged={loadFlow}
        />
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: { flex: 1 },
  container: { flex: 1, backgroundColor: colors.background.surface },
  contentContainer: { paddingBottom: spacing.xl },
  webContainer: { flex: 1, backgroundColor: colors.background.surface },
  webFormArea: { flex: 1 },
  webToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  webToolbarLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1, minWidth: 0 },
  webToolbarRight: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  toolbarTitle: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.textDark },
  statusBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm },
  statusBadgeDraft: { backgroundColor: '#FEF3C7' },
  runBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: spacing.md,
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.background.surface,
  },
  runBannerText: { flex: 1, fontSize: typography.fontSize.xs, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  runBannerClear: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  statusBadgePublished: { backgroundColor: '#DCFCE7' },
  statusBadgeText: { fontSize: 11, fontFamily: typography.fontFamily.semibold },
  statusBadgeTextDraft: { color: '#D97706' },
  statusBadgeTextPublished: { color: '#15803D' },
  circleButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  circlePrimaryButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  menuWrap: { position: 'relative' },
  // The dropdown renders inside a real RN <Modal> now (not an inline absolutely-positioned
  // sibling) — Modal always renders in its own top-level layer with unambiguous hit-testing,
  // which is what every OTHER working popover in this codebase already does (NodeConfigForm's
  // renderSimpleListModal, VariablePicker, ContactsList's assignment/filter menus). A prior
  // attempt used a same-tree absolutely-positioned View with either a backdrop TouchableOpacity
  // or a document mousedown listener for outside-click dismissal — both looked structurally
  // fine but never reliably received clicks on the menu items themselves on web, likely due to
  // RN Web's responder-based touch handling on TouchableOpacity interacting badly with a
  // manually-managed z-index stack; Modal sidesteps the whole problem.
  modalBackdrop: { flex: 1, backgroundColor: 'transparent' },
  // Anchored to the trigger button's measured on-screen position (measureInWindow, same
  // pattern as ContactsList.tsx's assignment/filter popovers) — falls back to the toolbar's
  // top-right corner only for the first render before a measurement exists.
  modalMenuPositioner: { position: 'absolute', top: 56, right: spacing.lg },
  menuDropdown: {
    minWidth: 170,
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    ...designTokens.shadows.md,
    paddingVertical: spacing.xs / 2,
  },
  menuItem: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  menuItemText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  circleButtonCaret: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: spacing.md, fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary },
  backButton: { padding: spacing.xs, marginRight: spacing.md },
  formContainer: { padding: spacing.md },
});
